import { MutableRefObject } from 'react';

import { Button, Input, Select, TypeSelectOption } from '@thefarmersfront/kitchen';
import { findItemByField } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { FormField, ModalSection } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { useStockReleaseStore } from '~/store';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import { TypeStockReleaseRegisterErrorFieldRefs } from '~/types/stock.types';

import styles from './StockReleaseRegisterSecondStepContent.module.scss';

/**
 * [재고관리 > 반출요청관리] 반출요청등록 모달 > STEP 2 > 파렛트 전표 정보
 */
const StockReleaseRegisterPalletInfo = ({
  errorFieldRefs,
  options,
}: {
  errorFieldRefs: MutableRefObject<TypeStockReleaseRegisterErrorFieldRefs>;
  options: Record<string, TypeSelectOption<string>[]> | null;
}) => {
  const { palletInfo, palletInfoState, setPalletInfo, setRegisterInfoState } = useStockReleaseStore(
    useShallow((state) => ({
      palletInfo: state.palletInfo,
      palletInfoState: state.palletInfoState,
      setPalletInfo: state.actions.setPalletInfo,
      setRegisterInfoState: state.actions.setRegisterInfoState,
    }))
  );

  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setPalletInfo({ zipCode, address });
      setRegisterInfoState({ palletInfoState: { zipCode: DEFAULT_INPUT_STATUS, address: DEFAULT_INPUT_STATUS } });
    },
  });

  return (
    <ModalSection className={styles['pallet-info']} title="파렛트 전표 정보">
      <FormField
        label="파렛트 종류"
        required
        contents={
          <Select
            ref={(el) => (errorFieldRefs.current['palletInfo.palletType'] = el)}
            size="small"
            placeholder="선택"
            maxWidth="148px"
            dropdownPortalTarget={document.body}
            shouldCloseOnNotInView
            options={options?.vendorReleasePalletType || []}
            value={findItemByField(options?.vendorReleasePalletType ?? [], { value: palletInfo?.palletType ?? '' })}
            status={palletInfoState.palletType.status}
            validationMessage={palletInfoState.palletType.message}
            onChange={(_: unknown, palletType: TypeSelectOption<string>) =>
              setPalletInfo({ palletType: palletType.value })
            }
            onClick={() => setRegisterInfoState({ palletInfoState: { palletType: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="하차지 물류코드"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['palletInfo.logisticsCode'] = el)}
            type="text"
            size="small"
            maxWidth="289px"
            placeholder="입력"
            count
            maxLength={10}
            value={palletInfo?.logisticsCode ?? ''}
            status={palletInfoState.logisticsCode.status}
            validationMessage={palletInfoState.logisticsCode.message}
            onChange={(e) => {
              setPalletInfo({ logisticsCode: e.target.value });
              setRegisterInfoState({ palletInfoState: { logisticsCode: DEFAULT_INPUT_STATUS } });
            }}
            onBlur={(e) => setPalletInfo({ logisticsCode: e.target.value.trim() })}
            onClick={() => setRegisterInfoState({ palletInfoState: { logisticsCode: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="하차지 담당자명"
        required
        contents={
          <Input
            type="text"
            size="small"
            maxWidth="289px"
            placeholder="입력"
            count
            maxLength={20}
            value={palletInfo?.managerName ?? ''}
            status={palletInfoState.managerName.status}
            validationMessage={palletInfoState.managerName.message}
            onChange={(e) => {
              setPalletInfo({ managerName: e.target.value });
              setRegisterInfoState({ palletInfoState: { managerName: DEFAULT_INPUT_STATUS } });
            }}
            onBlur={(e) => setPalletInfo({ managerName: e.target.value.trim() })}
            onClick={() => setRegisterInfoState({ palletInfoState: { managerName: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="하차지 담당자 연락처"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['palletInfo.managerPhoneNumber'] = el)}
            type="text"
            size="small"
            maxWidth="289px"
            placeholder="- 포함 입력"
            status={palletInfoState.managerPhoneNumber.status}
            validationMessage={palletInfoState.managerPhoneNumber.message}
            value={palletInfo?.managerPhoneNumber ?? ''}
            onChange={(e) => {
              const phoneNumber = e.target.value;
              if (validateNumberAndHyphenOnly(phoneNumber)) {
                setPalletInfo({ managerPhoneNumber: phoneNumber });
                setRegisterInfoState({ palletInfoState: { managerPhoneNumber: DEFAULT_INPUT_STATUS } });
              }
            }}
            onBlur={(e) => setPalletInfo({ managerPhoneNumber: e.target.value.trim() })}
            onClick={() => setRegisterInfoState({ palletInfoState: { managerPhoneNumber: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        ref={(el) => (errorFieldRefs.current['palletInfo.zipCode'] = el)}
        label="하차지 우편번호"
        required
        contents={
          <>
            <Input
              type="text"
              size="small"
              maxWidth="236px"
              disabled
              value={palletInfo?.zipCode ?? ''}
              status={palletInfoState.zipCode.status}
              validationMessage={palletInfoState.zipCode.message}
            />
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                handleZipCodeSearchOpen();
                setRegisterInfoState({
                  palletInfoState: { zipCode: DEFAULT_INPUT_STATUS, address: DEFAULT_INPUT_STATUS },
                });
              }}
            >
              검색
            </Button>
          </>
        }
        contentsClassName={styles['address-container']}
      />
      <FormField
        label="하차지 기본주소"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['palletInfo.address'] = el)}
            type="text"
            size="small"
            maxWidth="289px"
            disabled
            value={palletInfo?.address ?? ''}
            status={palletInfoState.address.status}
            validationMessage={palletInfoState.address.message}
          />
        }
      />
      <FormField
        label="하차지 상세주소"
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['palletInfo.detailAddress'] = el)}
            type="text"
            size="small"
            maxWidth="770px"
            placeholder="입력"
            count
            maxLength={50}
            value={palletInfo?.detailAddress ?? ''}
            onChange={(e) => setPalletInfo({ detailAddress: e.target.value })}
            onBlur={(e) => setPalletInfo({ detailAddress: e.target.value.trim() })}
          />
        }
      />
      <FormField
        label="하차지 담당자 이메일"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['palletInfo.managerEmail'] = el)}
            type="text"
            size="small"
            maxWidth="770px"
            placeholder="입력"
            count
            maxLength={50}
            value={palletInfo?.managerEmail ?? ''}
            status={palletInfoState.managerEmail.status}
            validationMessage={palletInfoState.managerEmail.message}
            onChange={(e) => {
              setPalletInfo({ managerEmail: e.target.value });
              setRegisterInfoState({ palletInfoState: { managerEmail: DEFAULT_INPUT_STATUS } });
            }}
            onBlur={(e) => setPalletInfo({ managerEmail: e.target.value.trim() })}
            onClick={() => setRegisterInfoState({ palletInfoState: { managerEmail: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
    </ModalSection>
  );
};

export default StockReleaseRegisterPalletInfo;
